'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Typography } from 'src/components';
import { SLASH_MENU_ITEMS } from '../../constants';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';

interface Position {
  x: number;
  y: number;
}

interface ComponentMenuProps {
  isOpen: boolean;
  position: Position;
  onSelect: (type: CriteriaComponentType) => void;
  onClose: () => void;
  allowedTypes?: CriteriaComponentType[];
}

const ComponentMenu: React.FC<ComponentMenuProps> = ({
  isOpen,
  position,
  onSelect,
  onClose,
  allowedTypes,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuDimensions, setMenuDimensions] = useState({
    width: 192,
    height: 256,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Filter menu items based on allowedTypes prop
  const filteredMenuItems = React.useMemo(() => {
    if (!allowedTypes || allowedTypes.length === 0) {
      return SLASH_MENU_ITEMS;
    }
    const filtered = SLASH_MENU_ITEMS.filter((item) =>
      allowedTypes.includes(item.type)
    );

    // Sort to put 'this_provider' and 'this_site' at the top if they exist
    return filtered.sort((a, b) => {
      const aIsSpecial = a.type === 'this_provider' || a.type === 'this_site';
      const bIsSpecial = b.type === 'this_provider' || b.type === 'this_site';

      if (aIsSpecial && !bIsSpecial) return -1;
      if (!aIsSpecial && bIsSpecial) return 1;

      // Both are special or both are not - sort alphabetically
      return a.label?.localeCompare(b.label);
    });
  }, [allowedTypes]);

  const colorMap: Record<string, string> = {
    sky: 'bg-sky-600',
    green: 'bg-green-600',
    amber: 'bg-amber-600',
    rose: 'bg-rose-600',
    cyan: 'bg-cyan-600',
    pink: 'bg-pink-600',
    purple: 'bg-purple-600',
    violet: 'bg-violet-600',
    blue: 'bg-blue-600',
    red: 'bg-red-600',
    yellow: 'bg-yellow-400',
    orange: 'bg-orange-400',
    teal: 'bg-teal-200',
    lime: 'bg-lime-600',
    neutral: 'bg-neutral-200',
  };

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setMenuDimensions({
        width: rect.width,
        height: rect.height,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0); // Reset to first item when menu opens
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) => (prev + 1) % filteredMenuItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(
            (prev) =>
              (prev - 1 + filteredMenuItems.length) % filteredMenuItems.length
          );
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          onSelect(filteredMenuItems[selectedIndex].type);
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, onSelect, onClose, filteredMenuItems]);

  const adjustedPosition = React.useMemo(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = menuDimensions.width;
    const menuHeight = menuDimensions.height;

    let x = position.x;
    let y = position.y;

    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 8;
    }

    if (y + menuHeight > viewportHeight) {
      y = position.y - menuHeight - 12;
    }

    x = Math.max(8, x);
    y = Math.max(8, y);

    return { x, y };
  }, [position, menuDimensions]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        ref={menuRef}
        className="fixed z-50 bg-white border border-neutral-200 rounded-lg shadow-lg py-2 w-48"
        style={{
          top: adjustedPosition.y,
          left: adjustedPosition.x,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-neutral-100">
          <Typography className="text-sm font-medium text-neutral-700">
            Add Component
          </Typography>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredMenuItems.map((item, index) => (
            <button
              key={item.type}
              onClick={() => onSelect(item.type)}
              className={`w-full px-3 py-2 text-left transition-colors ${
                index === selectedIndex
                  ? 'bg-blue-50 text-blue-900 border-r-2 border-blue-500'
                  : 'text-neutral-900 hover:bg-neutral-50'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <div
                    className={`w-3 h-3 mt-1 rounded-full ${
                      colorMap[item.color] || 'bg-sky-600'
                    }`}
                  />
                </div>
                <div className="flex flex-col">
                  <Typography
                    className={`text-sm font-medium ${
                      index === selectedIndex
                        ? 'text-blue-900'
                        : 'text-neutral-900'
                    }`}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    className={`text-xs ${
                      index === selectedIndex
                        ? 'text-blue-700'
                        : 'text-neutral-500'
                    }`}
                  >
                    {item.description}
                  </Typography>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

export default ComponentMenu;
